import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useMemo, useState, type JSX, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import clsx from 'clsx';
import { MoreHorizontal, Pencil, Trash2 } from 'lucide-react';
import { apiRequest, ApiError } from '../lib/api';
import { useToast } from '../lib/toast';
import { useAuthStore } from '../lib/auth-store';
import { useConfirm } from '../components/ui/Confirm';
import { formatDate } from '../lib/format';
import { Button } from '../components/ui/Button';
import { Skeleton } from '../components/ui/Skeleton';
import { GatedSection } from '../components/shared/GatedSection';
import { DetailHeader } from '../components/shared/DetailHeader';
import { ActivityPanel } from '../components/shared/ActivityPanel';
import {
  MeetingDrawer,
  type MeetingDrawerMode,
} from '../components/academics/MeetingDrawer';
import type {
  BatchRecord,
  MeetingReport,
} from '../components/academics/types';
import type { HouseholdRecord } from '../components/people/types';

// The backend agent is enriching GET /api/meetings/:id with embedded batch,
// household, and creator references. We declare each as optional so the page
// renders gracefully against either the enriched response or today's raw row.
interface BatchSummary {
  id: string;
  name: string;
}

interface HouseholdSummary {
  id: string;
  primaryContactName: string;
}

interface CreatorSummary {
  id: string;
  displayName: string | null;
  email: string | null;
}

interface MeetingDetail extends MeetingReport {
  batch?: BatchSummary | null;
  household?: HouseholdSummary | null;
  creator?: CreatorSummary | null;
}

export const Route = createFileRoute('/_authed/academics/meetings/$meetingId')({
  component: MeetingDetailPage,
});

const MANAGE_ROLES: ReadonlyArray<string> = [
  'super_admin',
  'academic_admin',
  'nalim',
  'teacher',
];

function MeetingDetailPage(): JSX.Element {
  const { meetingId } = Route.useParams();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { confirm } = useConfirm();
  const role = useAuthStore((s) => s.user?.primaryRole ?? null);
  const canEdit = role !== null && MANAGE_ROLES.includes(role);
  const canDelete = role === 'super_admin';

  const [editDrawer, setEditDrawer] = useState<MeetingDrawerMode | null>(null);
  const [overflowOpen, setOverflowOpen] = useState(false);

  const meetingQuery = useQuery({
    queryKey: ['meeting', 'detail', meetingId] as const,
    queryFn: ({ signal }) =>
      apiRequest<MeetingDetail>(
        `/api/meetings/${encodeURIComponent(meetingId)}`,
        { signal },
      ),
    retry: (failureCount, err) => {
      if (err instanceof ApiError && (err.status === 404 || err.status === 403)) {
        return false;
      }
      return failureCount < 2;
    },
  });

  // The lookup queries feed both the Context fallback rendering (when the
  // enriched batch/household embed isn't deployed yet) and the edit drawer's
  // selects, which need the full lists to populate options.
  const batchesQuery = useQuery({
    queryKey: ['batches', 'meetings-lookup'] as const,
    queryFn: ({ signal }) =>
      apiRequest<{ items: BatchRecord[] }>('/api/batches', {
        signal,
        query: { page: 1, pageSize: 100 },
      }),
    staleTime: 60_000,
  });

  const householdsQuery = useQuery({
    queryKey: ['households', 'meetings-lookup'] as const,
    queryFn: ({ signal }) =>
      apiRequest<{ items: HouseholdRecord[]; total: number }>('/api/households', {
        signal,
        query: { page: 1, pageSize: 100 },
      }),
    staleTime: 60_000,
  });

  const meeting = meetingQuery.data;

  const invalidate = (): void => {
    void queryClient.invalidateQueries({ queryKey: ['meeting', 'detail', meetingId] });
    void queryClient.invalidateQueries({ queryKey: ['meetings'] });
  };

  const deleteMutation = useMutation({
    mutationFn: () =>
      apiRequest<{ ok: true }>(`/api/meetings/${encodeURIComponent(meetingId)}`, {
        method: 'DELETE',
      }),
    onSuccess: () => {
      toast('Meeting deleted.', { variant: 'success' });
      void queryClient.invalidateQueries({ queryKey: ['meetings'] });
      void navigate({ to: '/academics/meetings' });
    },
    onError: (err: unknown) => {
      toast(err instanceof ApiError ? err.message : 'Could not delete meeting.', {
        variant: 'error',
      });
    },
  });

  const handleDelete = async (): Promise<void> => {
    if (!meeting) return;
    const ok = await confirm({
      title: 'Delete meeting?',
      description:
        'This meeting report will be removed. Anyone reviewing this conversation will no longer be able to find it.',
      confirmLabel: 'Delete',
      variant: 'danger',
    });
    if (!ok) return;
    setOverflowOpen(false);
    deleteMutation.mutate();
  };

  // Resolve names: prefer the enriched embed; fall back to lookups so the
  // detail page is useful even before the backend ships the join.
  const batchSummary = useMemo<BatchSummary | null>(() => {
    if (!meeting) return null;
    if (meeting.batch) return meeting.batch;
    if (!meeting.batchId) return null;
    const found = batchesQuery.data?.items.find((b) => b.id === meeting.batchId);
    return found ? { id: found.id, name: found.name } : { id: meeting.batchId, name: meeting.batchId.slice(0, 8) };
  }, [meeting, batchesQuery.data]);

  const householdSummary = useMemo<HouseholdSummary | null>(() => {
    if (!meeting) return null;
    if (meeting.household) return meeting.household;
    if (!meeting.householdId) return null;
    const found = householdsQuery.data?.items.find((h) => h.id === meeting.householdId);
    return found
      ? { id: found.id, primaryContactName: found.primaryContactName }
      : { id: meeting.householdId, primaryContactName: meeting.householdId.slice(0, 8) };
  }, [meeting, householdsQuery.data]);

  return (
    <div className="px-4 sm:px-6 py-6 max-w-screen-2xl mx-auto w-full">
      <GatedSection
        error={meetingQuery.error}
        title="Meeting"
        onRetry={() => meetingQuery.refetch()}
      >
        {meetingQuery.isLoading || !meeting ? (
          <div className="space-y-4">
            <Skeleton className="h-24 w-full" />
            <Skeleton className="h-16 w-full" />
            <Skeleton className="h-64 w-full" />
          </div>
        ) : (
          <div className="space-y-6">
            <DetailHeader
              title={`Meeting · ${formatDate(meeting.meetingAt, { withTime: true })}`}
              sublabel={
                <code className="rounded bg-[color:var(--color-surface)] px-1.5 py-0.5 font-mono text-[11px] text-[color:var(--color-text-muted)]">
                  {meeting.id}
                </code>
              }
              statusChip={
                <ContextChip
                  batchId={meeting.batchId}
                  householdId={meeting.householdId}
                />
              }
              backTo="/academics/meetings"
              actions={
                <>
                  {canEdit ? (
                    <Button
                      variant="secondary"
                      size="sm"
                      icon={<Pencil size={14} aria-hidden="true" />}
                      onClick={() => setEditDrawer({ kind: 'edit', id: meeting.id })}
                    >
                      Edit
                    </Button>
                  ) : null}
                  {canDelete ? (
                    <OverflowMenu
                      open={overflowOpen}
                      onOpenChange={setOverflowOpen}
                      pending={deleteMutation.isPending}
                      onDelete={() => {
                        void handleDelete();
                      }}
                    />
                  ) : null}
                </>
              }
            />

            <section className="grid gap-4 lg:grid-cols-2">
              <Card title="Summary">
                <p className="whitespace-pre-wrap text-sm text-[color:var(--color-text)]">
                  {meeting.summary}
                </p>
              </Card>

              <Card title="Attendees">
                {meeting.attendees.length === 0 ? (
                  <p className="text-sm text-[color:var(--color-text-muted)]">
                    No attendees recorded.
                  </p>
                ) : (
                  <ul className="flex flex-wrap gap-1.5">
                    {meeting.attendees.map((name) => (
                      <li
                        key={name}
                        className="inline-flex items-center rounded-full border border-[color:var(--color-border)] bg-[color:var(--color-surface)] px-2.5 py-1 text-xs font-medium text-[color:var(--color-text)]"
                      >
                        {name}
                      </li>
                    ))}
                  </ul>
                )}
              </Card>

              <Card title="Follow-ups" className="lg:col-span-2">
                {meeting.followUps && meeting.followUps.trim().length > 0 ? (
                  <p className="whitespace-pre-wrap text-sm text-[color:var(--color-text)]">
                    {meeting.followUps}
                  </p>
                ) : (
                  <p className="text-sm text-[color:var(--color-text-muted)]">
                    No follow-ups noted.
                  </p>
                )}
              </Card>

              <Card title="Context">
                {batchSummary ? (
                  <div className="flex items-center justify-between gap-3">
                    <div className="min-w-0">
                      <p className="text-xs uppercase tracking-wide text-[color:var(--color-text-muted)]">
                        Batch
                      </p>
                      <p className="mt-1 text-sm font-medium text-[color:var(--color-text)]">
                        {batchSummary.name}
                      </p>
                    </div>
                    <Link
                      to="/academics/batches/$batchId"
                      params={{ batchId: batchSummary.id }}
                      className="text-xs font-medium text-gold-700 hover:underline"
                    >
                      Open batch
                    </Link>
                  </div>
                ) : householdSummary ? (
                  <div className="flex items-center justify-between gap-3">
                    <div className="min-w-0">
                      <p className="text-xs uppercase tracking-wide text-[color:var(--color-text-muted)]">
                        Family
                      </p>
                      <p className="mt-1 text-sm font-medium text-[color:var(--color-text)]">
                        {householdSummary.primaryContactName}
                      </p>
                    </div>
                    <Link
                      to="/people/households/$householdId"
                      params={{ householdId: householdSummary.id }}
                      className="text-xs font-medium text-gold-700 hover:underline"
                    >
                      Open family
                    </Link>
                  </div>
                ) : (
                  <p className="text-sm text-[color:var(--color-text-muted)]">
                    Unlinked — this meeting is not tied to a batch or family.
                  </p>
                )}
              </Card>

              <Card title="Logged by">
                {meeting.creator ? (
                  <div>
                    <p className="text-sm font-medium text-[color:var(--color-text)]">
                      {meeting.creator.displayName ?? 'Unknown'}
                    </p>
                    {meeting.creator.email ? (
                      <p className="mt-0.5 text-xs text-[color:var(--color-text-muted)]">
                        {meeting.creator.email}
                      </p>
                    ) : null}
                    <p className="mt-2 text-xs text-[color:var(--color-text-muted)]">
                      Logged {formatDate(meeting.createdAt, { withTime: true })}
                    </p>
                  </div>
                ) : (
                  <div>
                    <p className="text-sm text-[color:var(--color-text-muted)]">
                      Created by user{' '}
                      <code className="font-mono text-[11px]">
                        {meeting.createdBy.slice(0, 8)}
                      </code>
                    </p>
                    <p className="mt-2 text-xs text-[color:var(--color-text-muted)]">
                      Logged {formatDate(meeting.createdAt, { withTime: true })}
                    </p>
                  </div>
                )}
              </Card>
            </section>

            <ActivityPanel
              targetType="meeting_report"
              targetId={meeting.id}
              title="Activity"
            />
          </div>
        )}
      </GatedSection>

      {canEdit && meeting ? (
        <MeetingDrawer
          mode={editDrawer}
          onClose={() => setEditDrawer(null)}
          initial={meeting}
          isLoadingInitial={false}
          loadError={null}
          batches={batchesQuery.data?.items ?? []}
          households={householdsQuery.data?.items ?? []}
          onSaved={() => {
            setEditDrawer(null);
            invalidate();
            toast('Meeting saved.', { variant: 'success' });
          }}
        />
      ) : null}
    </div>
  );
}

function ContextChip({
  batchId,
  householdId,
}: {
  batchId: string | null;
  householdId: string | null;
}): JSX.Element {
  let label = 'Unlinked';
  let tone =
    'bg-[color:var(--color-surface)] text-[color:var(--color-text-muted)]';
  if (batchId) {
    label = 'Batch';
    tone = 'bg-gold-50 text-gold-700 dark:bg-gold-700/20';
  } else if (householdId) {
    label = 'Family';
    tone = 'bg-[color:var(--color-success)]/15 text-[color:var(--color-success)]';
  }
  return (
    <span
      className={clsx(
        'inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold',
        tone,
      )}
    >
      {label}
    </span>
  );
}

function Card({
  title,
  children,
  className,
}: {
  title: string;
  children: ReactNode;
  className?: string;
}): JSX.Element {
  return (
    <div
      className={clsx(
        'rounded-xl border border-[color:var(--color-border)] bg-[color:var(--color-bg-elevated)] p-5',
        className,
      )}
    >
      <h3 className="text-xs font-semibold uppercase tracking-wide text-[color:var(--color-text-muted)]">
        {title}
      </h3>
      <div className="mt-3">{children}</div>
    </div>
  );
}

interface OverflowMenuProps {
  open: boolean;
  onOpenChange: (next: boolean) => void;
  pending: boolean;
  onDelete: () => void;
}

function OverflowMenu({
  open,
  onOpenChange,
  pending,
  onDelete,
}: OverflowMenuProps): JSX.Element {
  return (
    <div className="relative">
      <Button
        variant="ghost"
        size="sm"
        icon={<MoreHorizontal size={14} aria-hidden="true" />}
        onClick={() => onOpenChange(!open)}
        aria-haspopup="menu"
        aria-expanded={open}
        loading={pending}
      >
        More
      </Button>
      {open ? (
        <div
          role="menu"
          className="absolute right-0 z-10 mt-1 min-w-[160px] rounded-md border border-[color:var(--color-border)] bg-[color:var(--color-bg-elevated)] py-1 shadow-lg"
        >
          <button
            role="menuitem"
            type="button"
            onClick={onDelete}
            className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-[color:var(--color-danger)] hover:bg-[color:var(--color-danger)]/10 focus:outline-none"
          >
            <Trash2 size={14} aria-hidden="true" />
            Delete
          </button>
        </div>
      ) : null}
    </div>
  );
}
